import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  MenuItem,
  Container,
  Card,
  CardContent,
  Stack,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  ArrowBack,
  AdminPanelSettings,
  Person,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import { uiPT } from '../i18n';

interface User {
  _id: string;
  username: string;
  email: string;
  role: 'admin' | 'employee';
  isActive: boolean;
}

interface UserFormData {
  username: string;
  email: string;
  password: string;
  role: 'admin' | 'employee';
  isActive: boolean;
}

export default function UsersPage() {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [formData, setFormData] = useState<UserFormData>({
    username: '',
    email: '',
    password: '',
    role: 'employee',
    isActive: true,
  });
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteSuccess, setDeleteSuccess] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await api.get('/users');
      setUsers(response.data.data);
    } catch (error) {
      setError('Erro ao carregar usuários');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    try {
      if (editingUser) {
        await api.put(`/users/${editingUser._id}`, formData);
        setSuccess('Usuário atualizado com sucesso');
      } else {
        await api.post('/users', formData);
        setSuccess('Usuário criado com sucesso');
      }
      
      setDialogOpen(false);
      setEditingUser(null);
      setFormData({
        username: '',
        email: '',
        password: '',
        role: 'employee',
        isActive: true,
      });
      fetchUsers();
    } catch (error: any) {
      setError(error.response?.data?.message || 'Erro ao salvar usuário');
    }
  };

  const handleEdit = (user: User) => {
    setEditingUser(user);
    setFormData({
      username: user.username,
      email: user.email,
      password: '',
      role: user.role,
      isActive: user.isActive,
    });
    setDialogOpen(true);
  };

  const handleDeleteUser = async () => {
    if (!userToDelete) return;

    setDeleteLoading(true);
    setDeleteError(null);
    try {
      await api.delete(`/users/${userToDelete._id}`);
      setDeleteDialog(false);
      setDeleteSuccess(`${uiPT.dashboard.deleteUserSuccess.replace('!', '')} "${userToDelete.username}"!`);
      setUserToDelete(null);
      fetchUsers();
    } catch (error: any) {
      console.error("Error deleting user:", error);
      setDeleteError(error.response?.data?.message || uiPT.dashboard.deleteUserError);
    } finally {
      setDeleteLoading(false);
    }
  };

  const openDeleteDialog = (user: User) => {
    setUserToDelete(user);
    setDeleteDialog(true);
    setDeleteError(null);
    setDeleteSuccess(null);
  };

  return (
    <Box sx={{ minHeight: '100vh', background: (theme) => theme.palette.grey[100], py: 3 }}>
      <Container maxWidth="xl">
        <Stack spacing={3}>
          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton 
              onClick={() => navigate(-1)}
              sx={{ mr: 2 }}
            >
              <ArrowBack />
            </IconButton>
            <Typography variant="h4" sx={{ fontWeight: 800 }}>
              Gerenciar Usuários
            </Typography>
          </Box>

          {/* Success/Error Notifications */}
          {deleteSuccess && (
            <Alert 
              severity="success" 
              sx={{ borderRadius: 2 }}
              onClose={() => setDeleteSuccess(null)}
            >
              {deleteSuccess}
            </Alert>
          )}

          {deleteError && (
            <Alert 
              severity="error" 
              sx={{ borderRadius: 2 }}
              onClose={() => setDeleteError(null)}
            >
              {deleteError}
            </Alert>
          )}

          {error && (
            <Alert severity="error" sx={{ borderRadius: 2 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ borderRadius: 2 }}>
              {success}
            </Alert>
          )}

          {/* Stats Cards */}
          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
            <Box sx={{ flex: 1, minWidth: 250 }}>
              <Card 
                sx={{ 
                  background: (theme) => `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                  color: 'white',
                  borderRadius: 2,
                  boxShadow: (theme) => theme.shadows[3],
                }}
              >
                <CardContent>
                  <Typography variant="h4" component="div" sx={{ fontWeight: 800 }}>
                    {users.length}
                  </Typography>
                  <Typography variant="body2">
                    Total de Usuários
                  </Typography>
                </CardContent>
              </Card>
            </Box>
            <Box sx={{ flex: 1, minWidth: 250 }}>
              <Card 
                sx={{ 
                  background: (theme) => `linear-gradient(135deg, ${theme.palette.success.main}, ${theme.palette.success.dark})`,
                  color: 'white',
                  borderRadius: 2,
                  boxShadow: (theme) => theme.shadows[3],
                }}
              >
                <CardContent>
                  <Typography variant="h4" component="div" sx={{ fontWeight: 800 }}>
                    {users.filter(u => u.role === 'admin').length}
                  </Typography>
                  <Typography variant="body2">
                    Administradores
                  </Typography>
                </CardContent>
              </Card>
            </Box>
            <Box sx={{ flex: 1, minWidth: 250 }}>
              <Card 
                sx={{ 
                  background: 'linear-gradient(45deg, #ff9800, #f57c00)',
                  color: 'white',
                  borderRadius: 3,
                  boxShadow: '0 8px 25px rgba(255, 152, 0, 0.3)',
                }}
              >
                <CardContent>
                  <Typography variant="h4" component="div" sx={{ fontWeight: 600 }}>
                    {users.filter(u => u.role === 'employee').length}
                  </Typography>
                  <Typography variant="body2">
                    Funcionários
                  </Typography>
                </CardContent>
              </Card>
            </Box>
          </Box>
        </Stack>

        {/* Users Table */}
        <Card>
          <Box sx={{ p: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Lista de Usuários
            </Typography>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => setDialogOpen(true)}
              sx={{
                fontWeight: 600,
              }}
            >
              Novo Usuário
            </Button>
          </Box>
          
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ background: (theme) => `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})` }}>
                  <TableCell sx={{ fontWeight: 600 }}>Usuário</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Função</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((user) => (
                  <TableRow 
                    key={user._id}
                    sx={{
                      '&:hover': {
                        background: (theme) => `${theme.palette.primary.main}08`,
                      },
                    }}
                  >
                    <TableCell sx={{ fontWeight: 600 }}>{user.username}</TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {user.role === 'admin' ? (
                          <AdminPanelSettings sx={{ mr: 1, color: '#667eea' }} />
                        ) : (
                          <Person sx={{ mr: 1, color: '#4caf50' }} />
                        )}
                        {user.role === 'admin' ? 'Administrador' : 'Funcionário'}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box
                        sx={{
                          px: 2,
                          py: 0.5,
                          borderRadius: 2,
                          backgroundColor: user.isActive ? '#4caf50' : '#f44336',
                          color: 'white',
                          fontSize: '0.875rem',
                          fontWeight: 600,
                          display: 'inline-block',
                        }}
                      >
                        {user.isActive ? 'Ativo' : 'Inativo'}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <IconButton
                        onClick={() => handleEdit(user)}
                        sx={{
                          '&:hover': {
                            background: 'rgba(102, 126, 234, 0.1)',
                          },
                        }}
                      >
                        <Edit />
                      </IconButton>
                      <IconButton
                        onClick={() => openDeleteDialog(user)}
                        sx={{
                          '&:hover': {
                            background: 'rgba(244, 67, 54, 0.1)',
                          },
                        }}
                      >
                        <Delete />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      </Container>

      {/* User Dialog */}
      <Dialog 
        open={dialogOpen} 
        onClose={() => setDialogOpen(false)}
        slotProps={{
          paper: {
            sx: {
              borderRadius: 3,
              minWidth: 500,
            },
          }
        }}
      >
        <DialogTitle sx={{ fontWeight: 600, color: '#2c3e50' }}>
          {editingUser ? 'Editar Usuário' : 'Novo Usuário'}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <TextField
                label="Nome de Usuário"
                value={formData.username}
                onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                required
                fullWidth
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    '&:hover fieldset': {
                      borderColor: '#667eea',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#667eea',
                    },
                  },
                }}
              />
              
              <TextField
                label="Email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                fullWidth
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    '&:hover fieldset': {
                      borderColor: '#667eea',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#667eea',
                    },
                  },
                }}
              />
              
              <TextField
                label={editingUser ? 'Nova Senha (deixe em branco para manter)' : 'Senha'}
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required={!editingUser}
                fullWidth
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    '&:hover fieldset': {
                      borderColor: '#667eea',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#667eea',
                    },
                  },
                }}
              />
              
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  select
                  label="Função"
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value as 'admin' | 'employee' })}
                  required
                  fullWidth
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover fieldset': {
                        borderColor: '#667eea',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#667eea',
                      },
                    },
                  }}
                >
                  <MenuItem value="employee">Funcionário</MenuItem>
                  <MenuItem value="admin">Administrador</MenuItem>
                </TextField>
                
                <TextField
                  select
                  label="Status"
                  value={formData.isActive ? 'active' : 'inactive'}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.value === 'active' })}
                  required
                  fullWidth
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover fieldset': {
                        borderColor: '#667eea',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#667eea',
                      },
                    },
                  }}
                >
                  <MenuItem value="active">Ativo</MenuItem>
                  <MenuItem value="inactive">Inativo</MenuItem>
                </TextField>
              </Box>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button 
              onClick={() => setDialogOpen(false)}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
              }}
            >
              Cancelar
            </Button>
            <Button 
              type="submit"
              variant="contained"
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
              }}
            >
              {editingUser ? 'Atualizar' : 'Criar'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog}
        onClose={() => setDeleteDialog(false)}
        slotProps={{
          paper: {
            sx: {
              borderRadius: 3,
              minWidth: 400,
            },
          }
        }}
      >
        <DialogTitle sx={{ fontWeight: 600, color: "#d32f2f" }}>
          {uiPT.dashboard.deleteUserConfirmTitle}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            {uiPT.dashboard.deleteUserConfirmMessage}{" "}
            <strong>"{userToDelete?.username}"</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Email: {userToDelete?.email} | Função: {userToDelete?.role === 'admin' ? 'Administrador' : 'Funcionário'}
          </Typography>
          <Alert severity="warning" sx={{ mb: 2 }}>
            {uiPT.dashboard.deleteUserWarning}
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setDeleteDialog(false)}
            disabled={deleteLoading}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
            }}
          >
            {uiPT.dashboard.cancel}
          </Button>
          <Button 
            variant="contained"
            color="error"
            onClick={handleDeleteUser}
            disabled={deleteLoading}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
            }}
          >
            {deleteLoading ? uiPT.dashboard.deletingUser : uiPT.dashboard.delete}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
} 